import type { Block } from '@plentymarkets/shop-api';
import type { BlockVisibilityRegistry, UseBlocksVisibilityReturn } from './types';

/**
 * @description Composable for centralized block visibility control
 * Consolidates static content checks and runtime data state into a single visibility system
 * @returns useBlocksVisibility composable interface
 * @example
 * ``` ts
 * const { shouldShowBlock, registerBlockVisibility, unregisterBlockVisibility } = useBlocksVisibility();
 *
 * // In a block component that needs runtime visibility:
 * registerBlockVisibility(block.meta.uuid, hasRealData);
 *
 * // In EditableBlocks.vue:
 * v-if="shouldShowBlock(block, enableActions)"
 *
 * onBeforeUnmount(() => {
 *  clearRegistry();
 *  window.removeEventListener('beforeunload', handleBeforeUnload);
 * });
 * ```
 */
export const useBlocksVisibility: UseBlocksVisibilityReturn = () => {
  // NK added to allow certain blocks to be visible even if they have no static content, as their visibility is determined by runtime data (e.g. FooterCompanyData, FooterPaymentShippingIcons)
  const emptyContentAllowedBlocks = new Set(['FooterCompanyData', 'FooterPaymentShippingIcons', 'FooterFootnote']);

  // Reactive registry where blocks can opt-in to register their runtime visibility state
  const visibilityRegistry = useState<BlockVisibilityRegistry>('block-visibility-registry', () => ({}));

  // Track when hydration is complete and registry can be safely used
  const isHydrationComplete = useState<boolean>('block-visibility-hydration-complete', () => false);

  /**
   * Check if a block has static content (non-empty configuration)
   */
  const hasStaticContent = (block: Block): boolean => {
    const options = block?.content;
    return !(!options || (typeof options === 'object' && Object.keys(options).length === 0));
  };

  /**
   * Register a block's runtime visibility state
   * Blocks should call this in onMounted and unregister in onBeforeUnmount
   * @param uuid - Block's meta.uuid
   * @param hasData - Indicates if block has data to display
   */
  const registerBlockVisibility = (uuid: string, hasData: boolean) => {
    visibilityRegistry.value[uuid] = hasData;
  };

  /**
   * Clear all registered blocks from the visibility registry
   * Should be called when the page unmounts to prevent memory leaks
   */
  const clearRegistry = () => {
    visibilityRegistry.value = {};
  };

  /**
   * Check if a block is visible
   * @param block - The block to check
   * @returns true if block is visible (assumes true if not explicitly set to false)
   */
  const isBlockVisible = (block: Block): boolean => {
    return (block.configuration as Record<string, unknown>)?.visible !== false;
  };

  /**
   * Toggle a block's visibility state
   * @param block - The block to toggle visibility for
   */
  const toggleBlockVisibility = (block: Block): void => {
    if (!block) return;

    if (!block.configuration) {
      block.configuration = {
        visible: true,
      };
    }

    const blockConfig = block.configuration as Record<string, unknown>;
    blockConfig.visible = !isBlockVisible(block);
  };

  /**
   * Determine if a block should be shown
   * Checks both static configuration and registered runtime state
   * @param block - The block to check
   * @param isEditorModeEnabled - Whether editor mode is active (shows all blocks when true)
   * @returns true if block should be visible
   */
  const shouldShowBlock = (block: Block, isEditorModeEnabled = false): boolean => {
    if (!block?.meta) return false;

    if ((block.configuration as Record<string, unknown>)?.visible === false) {
      return false;
    }

    if (isEditorModeEnabled) {
      return true;
    }

    // NK If block has empty/static content but should still render in preview, allow it in visibility logic
    if (!hasStaticContent(block) && !emptyContentAllowedBlocks.has(block.name)) {  
      return false;
    }

    if (isHydrationComplete.value) {
      const runtimeVisibility = visibilityRegistry.value[block.meta.uuid];
      if (runtimeVisibility !== undefined) {
        return runtimeVisibility;
      }
    }

    return true;
  };

  return {
    shouldShowBlock,
    registerBlockVisibility,
    clearRegistry,
    isHydrationComplete,
    isBlockVisible,
    toggleBlockVisibility,
  };
};
